import React from "react";
import LegalPageLayout, { Section, SectionTitle, Paragraph, List } from "components/legal/LegalPageLayout.js";

export default () => {
  return (
    <LegalPageLayout
      productName="Group Kitty"
      productUrl="/products/group-kitty"
      title="Privacy Policy"
      lastUpdated="August 5, 2026"
    >
      <Section>
        <Paragraph>
          <strong>Spurnest Techlabs</strong> built the <strong>Group Kitty</strong> app (also available at{" "}
          <a href="https://groupkitty.spurnest.com" tw="text-primary-500 font-semibold">groupkitty.spurnest.com</a>
          ) as a trip expense-splitting service. This Privacy Policy explains how we collect, use, and protect your information when you use the Service on Android, iOS, or the web.
        </Paragraph>
        <Paragraph>
          If you choose to use our Service, you agree to the collection and use of information in relation to this policy. We will not use or share your information except as described here.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Information We Collect</SectionTitle>
        <List>
          <li>
            <strong>Google account info</strong> — if you sign in with Google (display name, email, profile photo, and account ID).
          </li>
          <li>
            <strong>Trip &amp; expense data</strong> — trip names, descriptions, currencies, member names, access codes, expense amounts, categories, payment modes, split shares, and related dates you enter.
          </li>
          <li>
            <strong>Purchase status</strong> — whether you have purchased the optional Remove Ads product (<code>isAdFree</code>).
          </li>
          <li>
            <strong>Local device data</strong> — cached trips, expenses, and preferences stored on your device for offline use.
          </li>
          <li>
            <strong>Advertising identifiers</strong> — on mobile, advertising IDs (such as GAID/IDFA) may be used by ad networks when ads are shown. On iOS we may request App Tracking Transparency permission.
          </li>
        </List>
        <Paragraph>
          You may also use Group Kitty in guest/local mode without signing in. In that case, trip data stays on your device unless you later sync with a signed-in account.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>How We Use Your Information</SectionTitle>
        <List>
          <li>To provide trip management, expense tracking, and settlement calculations.</li>
          <li>To sync your trips across devices when you sign in with Google.</li>
          <li>To remember Remove Ads purchase status.</li>
          <li>To serve advertisements (unless you have purchased Remove Ads).</li>
          <li>To maintain security of the Service (including optional Firebase App Check).</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>Permissions</SectionTitle>
        <Paragraph>
          Group Kitty requests only the permissions needed to operate. On Android this is primarily network access. On iOS we may request tracking permission for personalized ads. We do not require camera, contacts, location, or photo library access.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Third-Party Services</SectionTitle>
        <Paragraph>We use third-party services that may process information under their own policies:</Paragraph>
        <List>
          <li><strong>Google Sign-In / Firebase Authentication</strong> — account sign-in</li>
          <li><strong>Google Cloud Firestore</strong> — cloud storage of trips, expenses, and account preferences when signed in</li>
          <li><strong>Google AdMob</strong> — advertising on free (ad-supported) versions</li>
          <li><strong>Google Play / Apple App Store</strong> — processing of in-app purchases (Remove Ads)</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>Advertising</SectionTitle>
        <Paragraph>
          The free version of Group Kitty may show ads via Google AdMob (including banner, interstitial, app-open, and native formats). You can remove ads by purchasing the one-time Remove Ads product in the app. Ad networks may collect device and advertising identifiers as described in their privacy policies.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>In-App Purchases</SectionTitle>
        <Paragraph>
          Optional Remove Ads purchases are processed by Google Play or the Apple App Store. We do not receive or store your payment card details. We only store whether your account is marked ad-free.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Data Sharing Among Trip Members</SectionTitle>
        <Paragraph>
          Trip and expense data you create or join is visible to other members of that trip (for example via invite/access code). Do not enter sensitive personal information you do not want shared with your group.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Security</SectionTitle>
        <Paragraph>
          We strive to use commercially acceptable means to protect your information, including encrypted transit. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Children's Privacy</SectionTitle>
        <Paragraph>
          Group Kitty is not directed at children under 13. We do not knowingly collect personal information from children under 13. Contact us if you believe a child has provided us data.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Data Deletion</SectionTitle>
        <Paragraph>
          You may request deletion of your account and associated personal data by emailing{" "}
          <a href="mailto:info@spurnest.com" tw="text-primary-500 font-semibold">info@spurnest.com</a>.
          We will process requests within 30 days. Local guest data can be cleared by removing the app or clearing site data in your browser.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Contact Us</SectionTitle>
        <Paragraph>
          Spurnest Techlabs, Gandhinagar, Gujarat 382610, India.
          <br />
          Email: <a href="mailto:info@spurnest.com" tw="text-primary-500 font-semibold">info@spurnest.com</a>
          <br />
          Phone: <a href="tel:+918460407942" tw="text-primary-500 font-semibold">+91 84604 07942</a>
        </Paragraph>
      </Section>
    </LegalPageLayout>
  );
};
